import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useGetCompaniesQuery } from 'src/shared/api/company';
import {
  useGetCompanyParamsQuery,
  useLazyGetIntegrationUrlQuery,
  useSaveA3Mutation
} from 'src/shared/api/integrations/a3';
import { useGetIntegrationDataQuery } from 'src/shared/api/integrations/factorial';
import { getCompany, mapSalaryTypes, mapFormDataToA3Config, type A3Values } from './utils';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import { addToast } from 'src/old/actions';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fieldNameStep2 } from './consts';
import { getIsConnected } from '../utils/getIsConnected';
import { isEmpty } from 'lodash';

const integrationName = 'a3apiv2';

export const useA3Connection = () => {
  const form = useForm();
  const { values } = useFormState();
  const { subsidiary, companyCode } = values;
  const [selectedCompany, setSelectedCompany] = useState('');
  const [showSalary, setShowSalary] = useState(false);
  const [triggerIntegrationUrl] = useLazyGetIntegrationUrlQuery();
  const { data: companies } = useGetCompaniesQuery();
  const { data: integrationData, isLoading } = useGetIntegrationDataQuery();
  const isConnected = getIsConnected({ isLoading, integrationData, subsidiary, integrationName });
  const { data: companyParamsData } = useGetCompanyParamsQuery(
    {
      subsidiary,
      companyCode
    },
    { skip: !(companyCode && isConnected) }
  );

  const handleGetUrl = () => {
    void triggerIntegrationUrl({ subsidiary, companyCode });
  };

  const handleSelectedCompanyChange = (newCompany: string) => {
    setSelectedCompany(newCompany);

    const newCompanyObj = getCompany({ integrationData, companyName: newCompany });
    const salaryArr = newCompanyObj?.parameters.includeSalaryTypes;
    setShowSalary(Array.isArray(salaryArr) && salaryArr.length > 0);
  };

  const toggleShowSalary = () => {
    setShowSalary(prevShow => {
      const newShow = !prevShow;
      if (!newShow) {
        form.change(fieldNameStep2, []);
      }
      return newShow;
    });
  };

  useEffect(() => {
    if (!integrationData || !companies || !selectedCompany) {
      return;
    }

    const company = getCompany({ integrationData, companyName: selectedCompany });

    if (company) {
      form.change('subsidiary', company?.subsidiary);
      form.change('companyCode', company?.parameters.companyCode);
      form.change('includeSalaryTypes', company?.parameters.includeSalaryTypes);
      form.change('recordTransactions', company?.flowsEnabled.batchPaymentSync ?? false);

      if (!isEmpty(company?.fieldMap?.fieldMapping)) {
        form.change('withCustomFields', true);
        form.change('customFields', company?.fieldMap?.fieldMapping);
      } else {
        form.change('withCustomFields', false);
        form.change('customFields', null);
      }
    } else {
      form.change('companyCode', '');
      form.change('includeSalaryTypes', []);
      form.change('recordTransactions', false);

      form.change('withCustomFields', false);
      form.change('customFields', null);
    }
  }, [integrationData, form, companies, selectedCompany]);

  return {
    selectedCompany,
    showSalary,
    isConnected,
    resultGetSalaryType: mapSalaryTypes(companyParamsData?.salaryTypes),
    resultGetCustomFields: companyParamsData?.customFields,
    handleSelectedCompanyChange,
    toggleShowSalary,
    handleGetUrl
  };
};

export const useA3SaveIntegration = () => {
  const [saveConfig] = useSaveA3Mutation();
  const dispatch = useDispatch();
  const { t } = useTranslation('superAdmin');

  const saveIntegrationData = () => (formData: A3Values) => async () => {
    try {
      const payload = mapFormDataToA3Config(formData);
      await saveConfig(payload);
      dispatch(addToast({ ...toastTypes.success, defaultMessage: t('toasts.updated') }));
    } catch (error) {
      console.error(error);
    }
  };

  return saveIntegrationData;
};
